import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import { queryKeys } from '@/libs/queryKeys';
import { billingService } from '@/services/billingService';
import { creditService } from '@/services/creditService';

const CHECKOUT_KEY_PREFIX = 'decho_subscription_checkout_';

function newCheckoutKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `web-${crypto.randomUUID()}`;
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function idempotencyKey(planVersionId: string): string {
  const storageKey = `${CHECKOUT_KEY_PREFIX}${planVersionId}`;
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const created = newCheckoutKey();
  sessionStorage.setItem(storageKey, created);
  return created;
}

function clearCheckoutKeys(): void {
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith(CHECKOUT_KEY_PREFIX)) sessionStorage.removeItem(key);
  }
}

export function useBilling(pollForSubscription: boolean) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const optionsQuery = useQuery({
    queryKey: queryKeys.billing.options,
    queryFn: billingService.getOptions,
    staleTime: 60_000,
  });

  const currentQuery = useQuery({
    queryKey: queryKeys.billing.current,
    queryFn: billingService.getCurrentSubscription,
    refetchInterval: (query) => (pollForSubscription && query.state.data == null ? 3_000 : false),
  });

  React.useEffect(() => {
    if (currentQuery.data) clearCheckoutKeys();
  }, [currentQuery.data]);

  const subscriptionCheckout = useMutation({
    mutationFn: async (planVersionId: string) =>
      billingService.createSubscriptionCheckout(planVersionId, idempotencyKey(planVersionId)),
    onSuccess: ({ checkout_url: checkoutUrl }) => window.location.assign(checkoutUrl),
    onError: (error) =>
      addToast({
        title: 'Membership checkout unavailable',
        subtitle: getErrorMessage(error),
        type: 'error',
      }),
  });

  const creditCheckout = useMutation({
    mutationFn: creditService.createCheckout,
    onSuccess: ({ checkout_url: checkoutUrl }) => window.location.assign(checkoutUrl),
    onError: (error) =>
      addToast({
        title: 'Credit checkout unavailable',
        subtitle: getErrorMessage(error),
        type: 'error',
      }),
  });

  const cancelRenewal = useMutation({
    mutationFn: billingService.cancelRenewal,
    onSuccess: ({ message, subscription }) => {
      queryClient.setQueryData(queryKeys.billing.current, subscription);
      addToast({ title: 'Renewal canceled', subtitle: message, type: 'success' });
    },
    onError: (error) => {
      addToast({
        title: 'Could not cancel renewal',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    },
  });

  const managementSession = useMutation({
    mutationFn: billingService.createManagementSession,
    onSuccess: ({ portal_url: portalUrl }) => window.location.assign(portalUrl),
    onError: (error) =>
      addToast({
        title: 'Billing management unavailable',
        subtitle: getErrorMessage(error),
        type: 'error',
      }),
  });

  return {
    optionsQuery,
    currentQuery,
    subscriptionCheckout,
    creditCheckout,
    cancelRenewal,
    managementSession,
  };
}
